import React from "react";
import {
  Paper,
  Box,
  Typography,
  Chip,
  Avatar,
  Divider
} from "@mui/material";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import StarIcon from "@mui/icons-material/Star";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { PlayerImage } from "../player/player-image";
import { useSiteVarsFunctions } from "../../hooks/use-site-vars-functions";

export const MatchdayHighlights = ({ leaderboardData }) => {
  const { getPlayerPositionById, getTeamById, getCurrentGameweekEvent } = useSiteVarsFunctions();
  const currentEvent = getCurrentGameweekEvent();
  const currentWeek = currentEvent?.id || 1;

  if (!leaderboardData || leaderboardData.length === 0) {
    return null;
  }

  // 1. Top Manager of the Week (highest weekScore)
  let topManager = leaderboardData[0];
  let maxWeekScore = -1;
  leaderboardData.forEach((club) => {
    if (club.weekScore > maxWeekScore) {
      maxWeekScore = club.weekScore;
      topManager = club;
    }
  });

  // 2. Gameweek MVP (highest scoring player owned by a TLOG club)
  let mvpPlayer = null;
  let mvpClub = null;
  let maxPlayerPoints = -1;
  leaderboardData.forEach((club) => {
    if (Array.isArray(club.players)) {
      club.players.forEach((player) => {
        if (player.transfer !== "out" && (player.eventPoints || 0) > maxPlayerPoints) {
          maxPlayerPoints = player.eventPoints;
          mvpPlayer = player;
          mvpClub = club;
        }
      });
    }
  });

  // 3. Title Race Margin & Biggest Climber
  const leader = leaderboardData[0];
  const secondPlace = leaderboardData[1];
  const leaderLead = leader && secondPlace ? leader.totalScore - secondPlace.totalScore : 0;

  let biggestClimber = null;
  let maxClimb = 0;
  leaderboardData.forEach((club, idx) => {
    const currentPos = idx + 1;
    const climb = club.prevPosition ? club.prevPosition - currentPos : 0;
    if (climb > maxClimb) {
      maxClimb = climb;
      biggestClimber = { club, climb };
    }
  });

  return (
    <Paper
      elevation={2}
      sx={{
        mt: 2,
        mb: 2,
        p: 1.5,
        borderRadius: 2,
        backgroundColor: "background.paper",
        border: "1px solid rgba(0, 0, 0, 0.08)"
      }}
    >
      {/* Header Row */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
          mb: 1.25
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <AutoAwesomeIcon color="primary" fontSize="small" />
          <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: "0.95rem" }}>
            Gameweek Spotlights
          </Typography>
        </Box>
        <Chip
          size="small"
          label={`GW ${currentWeek}`}
          color="primary"
          variant="outlined"
          sx={{ fontWeight: "bold", height: 22, fontSize: "0.72rem" }}
        />
      </Box>

      <Divider sx={{ mb: 1.5 }} />

      {/* Spotlights Grid */}
      <Box
        sx={{
          width: "100%",
          display: "grid",
          gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
          gap: 1,
          boxSizing: "border-box"
        }}
      >
        {/* Tile 1: Top Manager (Col 1 on mobile, Col 1 on desktop) */}
        <Paper
          elevation={0}
          sx={{
            p: 1.25,
            borderRadius: 2,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            border: "1px solid rgba(255, 179, 0, 0.45)",
            background: "linear-gradient(135deg, rgba(255, 248, 225, 0.95) 0%, rgba(255, 255, 255, 1) 100%)",
            boxSizing: "border-box",
            minWidth: 0
          }}
        >
          {/* Header: Icon + Category + Points Chip */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.75 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <EmojiEventsIcon sx={{ color: "#f57c00", fontSize: 16 }} />
              <Typography
                variant="caption"
                sx={{
                  color: "#b26a00",
                  fontWeight: "bold",
                  fontSize: "0.68rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.3px"
                }}
              >
                Top Manager
              </Typography>
            </Box>
            <Chip
              label={`${topManager.weekScore} pts`}
              size="small"
              sx={{
                fontWeight: "bold",
                backgroundColor: "#ffb300",
                color: "#fff",
                height: 18,
                fontSize: "0.68rem",
                px: 0.25
              }}
            />
          </Box>

          {/* Content: Avatar + Names */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Avatar
              src={`/static/img/club-logos/120x120/${topManager.clubId}.png`}
              alt={topManager.clubName}
              sx={{ width: 34, height: 34, border: "1.5px solid #ffb300", flexShrink: 0 }}
            />
            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
              <Typography variant="body2" fontWeight="bold" sx={{ fontSize: "0.82rem", lineHeight: 1.2 }} noWrap>
                {topManager.managerName}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.72rem", lineHeight: 1.2 }} noWrap display="block">
                {topManager.clubName}
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Tile 2: Gameweek MVP (Col 2 on mobile, Col 2 on desktop) */}
        <Paper
          elevation={0}
          sx={{
            p: 1.25,
            borderRadius: 2,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            border: "1px solid rgba(46, 125, 50, 0.45)",
            background: "linear-gradient(135deg, rgba(232, 245, 233, 0.95) 0%, rgba(255, 255, 255, 1) 100%)",
            boxSizing: "border-box",
            minWidth: 0
          }}
        >
          {/* Header: Icon + Category + Points Chip */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.75 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <StarIcon sx={{ color: "#2e7d32", fontSize: 16 }} />
              <Typography
                variant="caption"
                sx={{
                  color: "#1b5e20",
                  fontWeight: "bold",
                  fontSize: "0.68rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.3px"
                }}
              >
                GW MVP
              </Typography>
            </Box>
            <Chip
              label={`${maxPlayerPoints > 0 ? maxPlayerPoints : 0} pts`}
              size="small"
              color="success"
              sx={{
                fontWeight: "bold",
                height: 18,
                fontSize: "0.68rem",
                px: 0.25
              }}
            />
          </Box>

          {/* Content: Photo + Player Details */}
          {mvpPlayer ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ width: 32, height: 38, overflow: "hidden", borderRadius: 1, flexShrink: 0 }}>
                <PlayerImage player={mvpPlayer} style={{ width: 32, height: 38 }} />
              </Box>
              <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                <Typography variant="body2" fontWeight="bold" sx={{ fontSize: "0.82rem", lineHeight: 1.2 }} noWrap>
                  {mvpPlayer.lastName}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.72rem", lineHeight: 1.2 }} noWrap display="block">
                  {getPlayerPositionById(mvpPlayer.positionId).name_short} · {getTeamById(mvpPlayer.teamId).name_short} ({mvpClub?.clubName})
                </Typography>
              </Box>
            </Box>
          ) : (
            <Typography variant="caption" color="text.secondary">
              No active MVP points yet
            </Typography>
          )}
        </Paper>

        {/* Tile 3: Title Race & Movers (Full width 2 cols on mobile, Col 3 on desktop) */}
        <Paper
          elevation={0}
          sx={{
            gridColumn: { xs: "1 / -1", md: "span 1" },
            p: 1.25,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            border: "1px solid rgba(25, 118, 210, 0.45)",
            background: "linear-gradient(135deg, rgba(227, 242, 253, 0.95) 0%, rgba(255, 255, 255, 1) 100%)",
            boxSizing: "border-box",
            minWidth: 0
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, flexGrow: 1 }}>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: "50%",
                backgroundColor: "#1976d2",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0
              }}
            >
              <TrendingUpIcon sx={{ fontSize: 18 }} />
            </Box>
            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  color: "#1565c0",
                  fontWeight: "bold",
                  fontSize: "0.68rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.3px",
                  display: "block"
                }}
              >
                Title Race Margin
              </Typography>
              <Typography variant="body2" fontWeight="bold" sx={{ fontSize: "0.82rem", lineHeight: 1.2 }} noWrap>
                {leader?.clubName}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.72rem", lineHeight: 1.2 }} noWrap display="block">
                {leaderLead > 0 ? `+${leaderLead} pts ahead of 2nd` : "Tied at 1st place"}
                {biggestClimber && ` · 🚀 ${biggestClimber.club.managerName} (+${biggestClimber.climb})`}
              </Typography>
            </Box>
          </Box>

          <Chip
            label={leaderLead > 0 ? `+${leaderLead} lead` : "Tied"}
            size="small"
            color="primary"
            variant="outlined"
            sx={{
              fontWeight: "bold",
              height: 20,
              fontSize: "0.7rem",
              flexShrink: 0
            }}
          />
        </Paper>
      </Box>
    </Paper>
  );
};
